import { StepDocuments } from '@/components/onboarding/StepDocuments'
import { useTenantNavigation } from '@/hooks/useTenantNavigation'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

// The certificate of insurance, on its own screen.
//
// An Emblem trucker reaches this after accepting their quote, when the COI is
// the only thing left outstanding — every other document was approved back in
// the wizard. Sending them to the full documents list instead showed a wall of
// already-approved slots with the one actionable box buried underneath.
//
// A self-insured driver whose wizard COI was rejected reaches the same route
// from the same card, so the copy stays path-neutral: it must not promise a
// certificate from Emblem to someone who carries their own policy.

export const Route = createFileRoute(
  '/_auth/carriers/$slug/driver-onboarding/insurance-certificate',
)({
  component: InsuranceCertificatePage,
  staticData: {
    title: 'route.insuranceCertificate',
  },
})

const COI_SLUGS = ['insurance_certificate'] as const

function InsuranceCertificatePage() {
  const { t } = useTranslation('ui')
  const { slug, navigate } = useTenantNavigation()

  // The certificate reaches the reviewer the moment it is uploaded, so this is
  // the driver saying they have finished attaching pages — not a submission. It
  // hands them back to the dashboard where their status is spelled out, rather
  // than leaving them on a form with nothing left to do.
  const handleDone = () => {
    navigate({ to: '/carriers/$slug/driver-onboarding/dashboard', params: { slug } })
  }

  // Handed to StepDocuments rather than rendered here, because only it knows
  // whether anything is outstanding: the route is reachable by bookmark once the
  // certificate is approved, and "this is the last thing we need from you"
  // directly above "nothing to upload here right now" reads as a bug.
  const header = (
    <div>
      <h1 className="text-2xl font-bold">{t('insuranceCertificate.title')}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{t('insuranceCertificate.subtitle')}</p>
    </div>
  )

  return <StepDocuments restrictToSlugs={COI_SLUGS} onDone={handleDone} header={header} />
}
